import { CSSProperties, FC, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { captureException } from '@sentry/react';
import { AxiosResponse } from 'axios';
import { TbDatabaseMinus } from 'react-icons/tb';

import { authorizedAxiosInstance } from '@/clients';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useQuery } from '@/hooks/useQuery';
import { useSavedSeries } from '@/hooks/useSavedSeries';
import {
    createMetricChartSeries,
    financialStatementEndpointForSymbol,
    MetricChart,
    metricChartCompanySetKey,
    metricChartSeriesVisibilityKey,
    normalizeMetricChartCompany,
} from '@/shared/components/data-visualization/recharts';
import type {
    MetricChartCompany,
    MetricChartComparisonDataset,
    MetricChartDataRow,
} from '@/shared/components/data-visualization/recharts';
import { FinancialValueDisplaySurfaceControl } from '@/shared/components/financial-values';
import { FullHeightFlexColumn } from '@/shared/components/layouts/flex';
import { MetricHeader } from '@/shared/components/layouts/headers';
import { MetricLineItems } from '@/shared/components/layouts/rows';
import EmptySection from '@/shared/components/layouts/views/EmptySection';
import { BaseSkeleton } from '@/shared/components/skeletons';
import {
    GlideDataGridTable,
    GlideDataGridTableExportDropdown,
    resolveFinancialStatementKind,
} from '@/shared/components/tables';
import { useUserPreferences } from '@/stores/database/entities/00-RBAC/99-UserPreferences';
import { SavedSeries, type SavedSeriesSectionKey, type SetStateMethod } from '@/types';
import { dayjs, FundamentalValuationsType } from '@/utilities';
import { formatFinancialLineItemLabel } from '@/utilities/helpers/formatFinancialLineItemLabel';

import DividendHistoryTable from './components/DividendHistoryTable';
import DividendHistoryTableExportDropdown from './components/DividendHistoryTable/DividendHistoryTableExportDropdown';
import FinancialAnalysisSplitView from './components/FinancialAnalysisSplitView';
import {
    financialStatementMetricTypes,
    financialStatementSectionLabel,
    resolveFinancialSeriesLineItems,
    resolveFirstAvailableFinancialLineItem,
} from './financialSeries';
import type {
    FinancialAnalysisRequestContext,
    FinancialAnalysisRequestParams,
    FinancialSeriesSnapshot,
    FinancialStatementDefaults,
    FinancialStatementPeriod,
} from './financialSeries';
import type { FinancialGrowthSection } from './revenueSegments';
import { useRevenueSegments } from './useRevenueSegments';

type FinancialStatementActionKind = 'chart' | 'table';
type FinancialStatementTableLayout = 'dividendEvents' | 'financialPeriods';

interface FinancialStatementActionWrapperArgs {
    actions?: ReactNode;
    children: ReactNode;
    component: string;
    kind: FinancialStatementActionKind;
}

export interface FinancialStatementAnalysisTabProps {
    allowComparisons?: boolean;
    chartCompany?: unknown;
    componentKey: SavedSeriesSectionKey;
    contentHeight?: string;
    defaultPeriod?: FinancialStatementPeriod;
    defaultLineItem: string;
    emptyDescription?: string;
    emptyLabel: string;
    endpoint: string;
    growthSection?: FinancialGrowthSection;
    headerInvestment?: unknown;
    lineItemFilter?: (lineItem: string, sampleRow: Record<string, unknown>) => boolean;
    metricType?: FundamentalValuationsType;
    metricTypeByLineItem?: Partial<Record<string, FundamentalValuationsType>>;
    normalizeRows?: (rows: Array<Record<string, unknown>>, period: FinancialStatementPeriod) => MetricChartDataRow[];
    renderActionWrapper?: (args: FinancialStatementActionWrapperArgs) => ReactNode;
    requestParams?: (context: FinancialAnalysisRequestContext) => FinancialAnalysisRequestParams;
    revenueSegments?: boolean;
    schema: string[];
    sectionTitle?: string;
    skeletonStyle?: CSSProperties;
    sourceData?: Array<Record<string, unknown>>;
    sourceDataLoading?: boolean;
    tableCompany?: unknown;
    tableLayout?: FinancialStatementTableLayout;
    tickerSymbol?: string;
    view: 'company' | 'investment';
}

const NON_METRIC_LINE_ITEMS = new Set([
    'acceptedDate',
    'calendarYear',
    'cik',
    'currency',
    'date',
    'filingDate',
    'fillingDate',
    'filingType',
    'finalLink',
    'fiscalDate',
    'fiscalQuarter',
    'fiscalYear',
    'id',
    'key',
    'link',
    'period',
    'reportedCurrency',
    'reportDate',
    'subkey',
    'symbol',
    'updated',
]);

const defaultFinancialStatementLineItemFilter = (lineItem: string, sampleRow: Record<string, unknown>) =>
    !NON_METRIC_LINE_ITEMS.has(lineItem) && typeof sampleRow[lineItem] === 'number';

const normalizeFinancialStatementRows = (
    rows: Array<Record<string, unknown>>,
    segmentedValue: string,
): MetricChartDataRow[] =>
    rows
        .map((row) => {
            const rawPeriod = row.period ?? row.fiscalYear ?? row.calendarYear;
            const periodYear = String(rawPeriod ?? '').match(/\b(19|20)\d{2}\b/)?.[0];
            const periodQuarter = String(rawPeriod ?? '').match(/Q([1-4])/i)?.[1];
            const fallbackDate = periodYear
                ? `${periodYear}-${String((Number(periodQuarter ?? 4) - 1) * 3 + 1).padStart(2, '0')}-01`
                : undefined;
            const date = typeof row.date === 'string' ? row.date : fallbackDate;
            const d = dayjs(date);
            const year = row.fiscalYear ?? row.calendarYear ?? periodYear ?? (d.isValid() ? d.year() : undefined);
            const quarter = periodQuarter ?? (d.isValid() ? d.quarter() : undefined);
            const periodLabel =
                segmentedValue === 'annual'
                    ? String(year ?? rawPeriod ?? '-')
                    : `Q${quarter ?? '-'} ${year ?? ''}`.trim();

            return {
                ...row,
                calendarYear: String(year),
                date,
                periodLabel,
            };
        })
        .sort((a, b) => new Date(String(a.date)).getTime() - new Date(String(b.date)).getTime());

export const FinancialStatementAnalysisTab: FC<FinancialStatementAnalysisTabProps> = ({
    allowComparisons = true,
    chartCompany,
    componentKey,
    contentHeight,
    defaultPeriod = 'annual',
    defaultLineItem,
    emptyDescription = 'Financial statement data will appear here when it is available.',
    emptyLabel,
    endpoint,
    growthSection,
    headerInvestment,
    lineItemFilter = defaultFinancialStatementLineItemFilter,
    metricType,
    metricTypeByLineItem,
    normalizeRows = normalizeFinancialStatementRows,
    renderActionWrapper,
    requestParams,
    revenueSegments = false,
    schema,
    sectionTitle,
    skeletonStyle,
    sourceData,
    sourceDataLoading = false,
    tableCompany,
    tableLayout = 'financialPeriods',
    tickerSymbol,
    view,
}) => {
    const { preferences, setPreference } = useUserPreferences();
    const financialStatementPreferences = preferences?.financialStatements;
    const financialDefaults = useMemo<FinancialStatementDefaults>(
        () => financialStatementPreferences?.defaults ?? {},
        [financialStatementPreferences?.defaults],
    );
    const {
        createSavedSeries,
        deleteSavedSeries,
        deletingSavedSeriesId,
        loadingSavedSeries,
        savedSeries,
        savingSeries,
        updateSavedSeries,
        updatingSavedSeriesId,
    } = useSavedSeries(componentKey);
    const [loading, setLoading] = useState(true);
    const [segmentedValue, setSegmentedValue] = useState<FinancialStatementPeriod>(defaultPeriod);
    const [startDate, setStartDate] = useState(dayjs().year(dayjs().year() - 15));
    const [endDate, setEndDate] = useState(dayjs());
    const [lineItems, setLineItems] = useState<string[]>([]);
    const [chartedLineItem, setChartedLineItem] = useState('');
    const [tags, setTags] = useState<string[]>([]);
    const [data, setData] = useState<MetricChartDataRow[]>([]);
    const [sourceRows, setSourceRows] = useState<Array<Record<string, unknown>>>([]);
    const [comparisonCompanies, setComparisonCompanies] = useState<MetricChartCompany[]>([]);
    const [comparisonData, setComparisonData] = useState<MetricChartComparisonDataset[]>([]);
    const [hiddenSeriesKeys, setHiddenSeriesKeys] = useState<string[]>([]);
    const [resolvedFocusLineItem, setResolvedFocusLineItem] = useState<string>();
    const hasLoadedDataRef = useRef(false);
    const preferencesHydratedRef = useRef(false);
    const { token } = useAntDesignTheme();
    const query = useQuery();
    const focusLineItemQuery = query.get('focusLineItem');
    const lineItemCandidatesQuery = query.get('lineItemCandidates');
    const lineItemsQuery = query.get('lineItems');
    const years = startDate && endDate ? Number(dayjs(endDate).get('year') - dayjs(startDate).get('year')) : 0;
    const safeYears = Math.max(1, years);
    const statementKind = resolveFinancialStatementKind(componentKey);
    const financialRowGroups = useRevenueSegments({
        enabled: Boolean(statementKind || growthSection),
        growthSection,
        includeRevenueSegments: revenueSegments,
        limit: segmentedValue === 'annual' ? safeYears : safeYears * 4,
        parentLineItems: schema,
        periodKind: segmentedValue,
        statementKind,
        symbol: tickerSymbol,
    });
    const primaryCompany = useMemo(
        () => normalizeMetricChartCompany(chartCompany ?? headerInvestment, tickerSymbol),
        [chartCompany, headerInvestment, tickerSymbol],
    );
    const comparisonCompanySetKey = metricChartCompanySetKey(comparisonCompanies);

    useEffect(() => {
        if (!preferences || preferencesHydratedRef.current) {
            return;
        }

        const hydratedPeriod = financialDefaults.period ?? defaultPeriod;
        const dateRangeYears = financialDefaults.dateRangeYears ?? 15;
        const defaultLineItems = financialDefaults.lineItems?.[componentKey] ?? [];
        const nextEndDate = dayjs();

        setSegmentedValue(hydratedPeriod);
        setEndDate(nextEndDate);
        setStartDate(nextEndDate.subtract(dateRangeYears, 'year'));

        if (!lineItemsQuery && defaultLineItems.length) {
            setTags(defaultLineItems.slice(0, 5));
            setChartedLineItem(defaultLineItems[0]);
        }

        preferencesHydratedRef.current = true;
    }, [componentKey, defaultPeriod, financialDefaults, lineItemsQuery, preferences]);

    useEffect(() => {
        const controller = new AbortController();

        const applyFinancialStatementRows = (rows: Array<Record<string, unknown>>) => {
            if (!rows.length) {
                setData([]);
                setLineItems([]);
                setSourceRows([]);
                setResolvedFocusLineItem(undefined);
                return;
            }

            const normalizedRows = normalizeRows(rows, segmentedValue);
            const metricKeys = Array.from(new Set(normalizedRows.flatMap((row) => Object.keys(row)))).filter((key) =>
                normalizedRows.some((row) => lineItemFilter(key, row)),
            );
            const initialKey = metricKeys.includes(defaultLineItem) ? defaultLineItem : metricKeys[0];
            const requestedCandidates = lineItemCandidatesQuery?.split(',').filter(Boolean) ?? [];
            const resolvedCandidate = resolveFirstAvailableFinancialLineItem(metricKeys, requestedCandidates);
            const requestedTags = resolvedCandidate
                ? [resolvedCandidate]
                : lineItemsQuery
                  ? lineItemsQuery.split(',').filter(Boolean)
                  : (financialDefaults.lineItems?.[componentKey] ?? []);
            const requestedChartLineItem =
                resolvedCandidate ??
                (focusLineItemQuery && metricKeys.includes(focusLineItemQuery) ? focusLineItemQuery : undefined);
            const requestedFocusLineItem = focusLineItemQuery ?? requestedChartLineItem;
            const hasExplicitLineItemRequest = Boolean(lineItemsQuery || lineItemCandidatesQuery);

            setData(normalizedRows);
            setLineItems(metricKeys);
            setSourceRows(rows);
            setResolvedFocusLineItem(requestedFocusLineItem);
            setChartedLineItem((current) =>
                requestedChartLineItem
                    ? requestedChartLineItem
                    : metricKeys.includes(current)
                      ? current
                      : (initialKey ?? ''),
            );
            setTags((current) =>
                resolveFinancialSeriesLineItems({
                    available: metricKeys,
                    current: hasExplicitLineItemRequest ? [] : current,
                    fallback: initialKey,
                    requested: requestedTags,
                }),
            );
        };

        const loadFinancialStatement = async () => {
            if (sourceData !== undefined) {
                setLoading(sourceDataLoading);

                if (!sourceDataLoading) {
                    applyFinancialStatementRows(sourceData);
                    hasLoadedDataRef.current = true;
                }

                return;
            }

            if (!tickerSymbol) {
                setData([]);
                setLineItems([]);
                setSourceRows([]);
                setLoading(false);
                return;
            }

            try {
                if (!hasLoadedDataRef.current) {
                    setLoading(true);
                }

                const limit = segmentedValue === 'annual' ? safeYears : safeYears * 4;
                const params = requestParams?.({
                    endDate: endDate.format('YYYY-MM-DD'),
                    limit,
                    period: segmentedValue,
                    startDate: startDate.format('YYYY-MM-DD'),
                }) ?? {
                    limit,
                    period: segmentedValue,
                };
                const response: AxiosResponse<Array<Record<string, unknown>>> = await authorizedAxiosInstance.get(
                    endpoint,
                    {
                        params,
                        signal: controller.signal,
                    },
                );

                if (controller.signal.aborted) {
                    return;
                }

                if (response.status !== 200 || !response.data?.length) {
                    setData([]);
                    setLineItems([]);
                    setSourceRows([]);
                    return;
                }

                applyFinancialStatementRows(response.data);
            } catch (error) {
                if (!controller.signal.aborted) {
                    captureException(error);
                }
            } finally {
                if (!controller.signal.aborted) {
                    hasLoadedDataRef.current = true;
                    setLoading(false);
                }
            }
        };

        void loadFinancialStatement();

        return () => controller.abort();
    }, [
        componentKey,
        defaultLineItem,
        endpoint,
        endDate,
        financialDefaults.lineItems,
        focusLineItemQuery,
        lineItemFilter,
        lineItemCandidatesQuery,
        lineItemsQuery,
        normalizeRows,
        requestParams,
        safeYears,
        segmentedValue,
        sourceData,
        sourceDataLoading,
        startDate,
        tickerSymbol,
    ]);

    useEffect(() => {
        const comparisonCompanySymbols = comparisonCompanySetKey.split('|').filter(Boolean);

        if (sourceData !== undefined || !comparisonCompanySymbols.length || !tickerSymbol) {
            setComparisonData([]);
            return;
        }

        const controller = new AbortController();

        const loadComparisonStatements = async () => {
            const datasets = await Promise.all(
                comparisonCompanySymbols.map(async (symbol): Promise<MetricChartComparisonDataset | undefined> => {
                    try {
                        const comparisonEndpoint = financialStatementEndpointForSymbol(endpoint, tickerSymbol, symbol);
                        const limit = segmentedValue === 'annual' ? safeYears : safeYears * 4;
                        const params = requestParams?.({
                            endDate: endDate.format('YYYY-MM-DD'),
                            limit,
                            period: segmentedValue,
                            startDate: startDate.format('YYYY-MM-DD'),
                        }) ?? {
                            limit,
                            period: segmentedValue,
                        };
                        const response: AxiosResponse<Array<Record<string, unknown>>> =
                            await authorizedAxiosInstance.get(comparisonEndpoint, {
                                params,
                                signal: controller.signal,
                            });

                        return {
                            company: { companyName: symbol, symbol },
                            data:
                                response.status === 200 && response.data?.length
                                    ? normalizeRows(response.data, segmentedValue)
                                    : [],
                        };
                    } catch (error) {
                        if (!controller.signal.aborted) {
                            captureException(error);
                        }

                        return undefined;
                    }
                }),
            );

            if (!controller.signal.aborted) {
                setComparisonData(
                    datasets.filter((dataset): dataset is MetricChartComparisonDataset => Boolean(dataset)),
                );
            }
        };

        void loadComparisonStatements();

        return () => controller.abort();
    }, [
        comparisonCompanySetKey,
        endDate,
        endpoint,
        normalizeRows,
        requestParams,
        safeYears,
        segmentedValue,
        sourceData,
        startDate,
        tickerSymbol,
    ]);

    useEffect(() => {
        const validSeriesKeys = new Set(
            createMetricChartSeries(tags, primaryCompany, comparisonCompanies).map((series) =>
                metricChartSeriesVisibilityKey(series.company.symbol, series.lineItem),
            ),
        );

        setHiddenSeriesKeys((current) => {
            const next = current.filter((key) => validSeriesKeys.has(key));

            return next.length === current.length ? current : next;
        });
    }, [comparisonCompanies, primaryCompany, tags]);

    const filteredLineItems = lineItems.filter((lineItem) => lineItemFilter(lineItem, data[0] ?? {}));
    const lineItemLabels = useMemo(
        () => Object.fromEntries(lineItems.map((lineItem) => [lineItem, formatFinancialLineItemLabel(lineItem)])),
        [lineItems],
    );
    const resolvedMetricTypeByLineItem = useMemo(
        () => ({ ...financialStatementMetricTypes, ...metricTypeByLineItem }),
        [metricTypeByLineItem],
    );
    const hasRenderableData = tags.length !== 0 && data.length !== 0 && lineItems.length !== 0;
    const resolvedMetricType = resolvedMetricTypeByLineItem[chartedLineItem] ?? metricType;
    const seriesSnapshot = useMemo<FinancialSeriesSnapshot>(
        () => ({
            comparisonCompanies,
            endDate: endDate.toISOString(),
            lineItems: tags,
            period: segmentedValue as FinancialStatementPeriod,
            sectionKey: componentKey,
            startDate: startDate.toISOString(),
        }),
        [comparisonCompanies, componentKey, endDate, segmentedValue, startDate, tags],
    );

    const applySavedSeries = (series: SavedSeries) => {
        const definition = series.definition;

        setSegmentedValue(definition.period);
        setStartDate(dayjs(definition.startDate));
        setEndDate(dayjs(definition.endDate));
        setTags(definition.lineItems.slice(0, 5));
        setChartedLineItem(definition.lineItems[0] ?? '');
        setComparisonCompanies(definition.comparisonCompanies);
        setHiddenSeriesKeys([]);
    };

    const updateFinancialDefaults = (defaults: FinancialStatementDefaults) => {
        setPreference('financialStatements.defaults', defaults);
        const nextEndDate = dayjs();
        const preferredLineItems = defaults.lineItems?.[componentKey] ?? [];

        setSegmentedValue(defaults.period ?? 'annual');
        setEndDate(nextEndDate);
        setStartDate(nextEndDate.subtract(defaults.dateRangeYears ?? 15, 'year'));

        if (preferredLineItems.length) {
            setTags(preferredLineItems.slice(0, 5));
            setChartedLineItem(preferredLineItems[0]);
        }

        setHiddenSeriesKeys([]);
    };

    const handlePeriodChange: SetStateMethod<string> = (value) => {
        setSegmentedValue((currentValue) => {
            const nextValue = typeof value === 'function' ? value(currentValue) : value;

            return nextValue === 'quarter' ? 'quarter' : 'annual';
        });
    };

    const withActionWrapper = (kind: FinancialStatementActionKind, children: ReactNode, actions?: ReactNode) => {
        const component = `${kind}-${componentKey}`;

        return renderActionWrapper ? renderActionWrapper({ actions, children, component, kind }) : children;
    };

    if (hasRenderableData) {
        return (
            <FullHeightFlexColumn
                style={
                    contentHeight
                        ? { height: 'auto', marginInline: '-1.5rem', overflow: 'visible' }
                        : { marginInline: '-1.5rem' }
                }
            >
                <MetricHeader
                    company={primaryCompany}
                    comparisonCompanies={comparisonCompanies}
                    tags={tags}
                    setTags={setTags}
                    setChartedLineItem={setChartedLineItem}
                    lineItems={filteredLineItems}
                    lineItemLabels={lineItemLabels}
                    startDate={startDate}
                    endDate={endDate}
                    setStartDate={setStartDate}
                    setEndDate={setEndDate}
                    segmentedValue={segmentedValue}
                    setSegmentedValue={handlePeriodChange}
                    investment={headerInvestment}
                    onComparisonCompaniesChange={allowComparisons ? setComparisonCompanies : undefined}
                    view={view}
                />
                <MetricLineItems
                    companies={primaryCompany ? [primaryCompany, ...comparisonCompanies] : comparisonCompanies}
                    deletingSavedSeriesId={deletingSavedSeriesId}
                    financialDefaults={financialDefaults}
                    financialSeriesSnapshot={seriesSnapshot}
                    hiddenSeriesKeys={hiddenSeriesKeys}
                    loadingSavedSeries={loadingSavedSeries}
                    lineItemLabels={lineItemLabels}
                    onApplySeries={applySavedSeries}
                    onComparisonCompaniesChange={setComparisonCompanies}
                    onCreateSeries={(name, definition) =>
                        createSavedSeries({
                            definition,
                            name,
                            visibility: 'personal',
                        })
                    }
                    onDeleteSeries={deleteSavedSeries}
                    onUpdateSeries={(id, name) => updateSavedSeries(id, { name })}
                    onFinancialDefaultsChange={updateFinancialDefaults}
                    onHiddenSeriesKeysChange={setHiddenSeriesKeys}
                    savedSeries={savedSeries}
                    savingSeries={savingSeries}
                    updatingSavedSeriesId={updatingSavedSeriesId}
                    tags={tags}
                    setTags={setTags}
                    view={view}
                />
                <FinancialAnalysisSplitView
                    height={contentHeight}
                    chart={withActionWrapper(
                        'chart',
                        <MetricChart
                            company={primaryCompany}
                            comparisonCompanies={comparisonCompanies}
                            comparisonData={comparisonData}
                            data={data}
                            hiddenSeriesKeys={hiddenSeriesKeys}
                            lineItemLabels={lineItemLabels}
                            tags={tags}
                            chartedLineItem={chartedLineItem}
                            segmentedValue={segmentedValue}
                            setTags={setTags}
                            setSegmentedValue={handlePeriodChange}
                            title={sectionTitle ?? financialStatementSectionLabel(componentKey)}
                            type={resolvedMetricType}
                            typeByLineItem={resolvedMetricTypeByLineItem}
                        />,
                    )}
                    table={withActionWrapper(
                        'table',
                        tableLayout === 'dividendEvents' ? (
                            <DividendHistoryTable data={sourceRows} />
                        ) : (
                            <GlideDataGridTable
                                company={tableCompany}
                                data={data}
                                segmentedValue={segmentedValue}
                                setSegmentedValue={handlePeriodChange}
                                statementKind={statementKind}
                                type={metricType}
                                typeByLineItem={resolvedMetricTypeByLineItem}
                                financialRowGroups={financialRowGroups}
                                focusLineItem={resolvedFocusLineItem ?? focusLineItemQuery ?? undefined}
                                schema={schema}
                                height='100%'
                            />
                        ),
                        <>
                            <FinancialValueDisplaySurfaceControl />
                            {tableLayout === 'dividendEvents' ? (
                                <DividendHistoryTableExportDropdown company={tableCompany} data={sourceRows} />
                            ) : (
                                <GlideDataGridTableExportDropdown
                                    company={tableCompany}
                                    data={data}
                                    segmentedValue={segmentedValue}
                                    schema={schema}
                                />
                            )}
                        </>,
                    )}
                />
            </FullHeightFlexColumn>
        );
    }

    if (loading) {
        return <BaseSkeleton fade style={skeletonStyle} paragraph={{ rows: 40 }} title />;
    }

    return (
        <EmptySection
            height='100%'
            icon={<TbDatabaseMinus size='1.25rem' style={{ color: token.colorTextTertiary }} />}
            label={emptyLabel}
            description={emptyDescription}
        />
    );
};

export { default as FinancialAnalysisSplitView } from './components/FinancialAnalysisSplitView';
